/**
 * ANIMATION CONFIGURATION
 * Central config for all animations and interactions across the site.
 * 
 * HOW TO USE:
 * - Set enabled flags to false to disable specific animation groups
 * - Adjust durations/delays in the respective objects
 * - Counter values can be modified in the counters object
 */

export const ANIMATION_CONFIG = {
  // ========================================
  // FEATURE FLAGS - Toggle animation groups
  // ========================================
  enabled: {
    hero: true,           // Hero section animations & counters
    cards: true,          // Card hover effects & entrance animations
    scroll: true,         // Scroll-triggered fade-in animations
    stickyBar: true,      // Sticky CTA bar at bottom of page
    ripple: true,         // Button click ripple effect
    stagger: true,        // Staggered entrance animations
    howItWorks: true,     // Scroll-based step highlighting
    blogBadges: true,     // Automatic new/featured badges
    librarySearch: true,  // Instant search filtering animations
  },

  // ========================================
  // TIMING - Animation durations (ms)
  // ========================================
  durations: {
    fast: 150,
    normal: 200,
    slow: 300,
    entrance: 500,
    stagger: 100,         // Delay between staggered items
  },

  // ========================================
  // EASING FUNCTIONS
  // ========================================
  easing: {
    default: 'cubic-bezier(0.16, 1, 0.3, 1)',
    bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // ========================================
  // SCROLL THRESHOLDS
  // ========================================
  scroll: {
    stickyBarThreshold: 0.65,   // Show sticky bar at 65% scroll
    entranceOffset: '-50px',     // Trigger entrance animation offset
  },

  // ========================================
  // HERO COUNTERS - Values and labels
  // To change counters, edit these values
  // ========================================
  counters: {
    experience: { value: 10, suffix: '+', label: 'שנות ניסיון בתשתיות, מפעלים ו-Data Centers' },
    trainings: { value: 100, suffix: '+', label: 'משתתפים בהדרכות בשנה' },
    clients: { value: 50, suffix: '+', label: 'לקוחות פעילים' },
    availability: { value: 24, suffix: '/7', label: 'זמינות לחירום', isStatic: true },
  },

  // ========================================
  // STICKY BAR CONFIG (texts moved to i18n - common.json)
  // ========================================
  stickyBar: {
    whatsappNumber: '972528300088',
    sessionStorageKey: 'cta_clicked',
  },

  // ========================================
  // BADGE CONFIG
  // ========================================
  badges: {
    newDaysThreshold: 30,       // Days to show "חדש" badge on blog posts
    recentDaysThreshold: 14,    // Days to show "חדש" badge on library items
  },
} as const;

// Type exports for better IDE support
export type AnimationConfig = typeof ANIMATION_CONFIG;
